// Single source of truth for page metadata, used by <Seo /> at runtime and by
// scripts/prerender.mjs at build time.
export const SITE_URL = "https://teachingpathways.com.au";
export const SITE_NAME = "Teaching Pathways";
export const DEFAULT_IMAGE = `${SITE_URL}/logo-full.png`;

export const DEFAULT_TITLE = "Teaching Pathways | Placement, employability and mentoring support for teachers";
export const DEFAULT_DESCRIPTION =
  "Teaching Pathways helps Australian pre-service teachers land jobs and thrive on placement with mentoring, workshops, interview prep and KSC support.";

export const PAGES = {
  "/": {
    title: "Teaching Pathways | Pre-service teacher placement, mentoring & employability support",
    description: DEFAULT_DESCRIPTION,
    keywords: "pre-service teacher, pre-service teacher mentoring, graduate teacher, teaching placement, teaching prac, first teaching job, teacher interview preparation, Teaching Pathways",
    heading: "Learn. Mentor. Grow.",
    summary: [
      "Teaching Pathways is an Australian professional learning and mentoring service for pre-service teachers, graduate teachers, international pre-service teachers, mentor teachers, schools and universities.",
      "We support teaching placement (prac) preparation, mentor teacher relationships, behaviour management for student teachers, teaching job applications, teaching resumes and cover letters, key selection criteria and mock teaching interviews.",
      "Programs include live workshops, one-to-one coaching, a tutoring marketplace, downloadable resources and editable workshop slide decks for mentors and schools.",
    ],
  },
  "/about": {
    title: "About Us | Teaching Pathways",
    description:
      "Experienced teachers, mentors and university educators supporting pre-service and international teachers across Australia and NZ through placement and into their first job.",
    keywords: "Teaching Pathways, teacher mentoring, pre-service teacher mentoring, graduate teacher support, mentor teachers Australia",
    heading: "About Us",
    summary: [
      "Teaching Pathways is a team of experienced teachers, mentors and university educators supporting pre-service and international teachers across Australia and New Zealand through placement and into their first job.",
      "We work with pre-service teachers, graduate teachers, international students in teaching degrees, mentor teachers, schools and universities.",
    ],
  },
  "/future-teachers": {
    title: "Teaching placement support for pre-service teachers | Teaching Pathways",
    description:
      "Teaching placement tips, placement checklists, mentor teacher relationship advice and behaviour management strategies for student teachers — how to prepare for teaching placement with confidence.",
    keywords: "teaching placement tips, how to prepare for teaching placement, placement checklist, mentor teacher relationship, behaviour management for student teachers, pre-service teacher, teaching prac",
    heading: "Future teachers",
    summary: [
      "Practical preparation for teaching placement: what to pack, what to read, how to meet your mentor teacher and how to make a strong first impression on prac.",
      "Covers placement checklists, classroom observation templates, reflective journalling, behaviour management for student teachers and managing placement nerves.",
    ],
  },
  "/international-teachers": {
    title: "International pre-service teachers in Australia | Teaching Pathways",
    description:
      "Support for international pre-service teachers and international students in teaching degrees in Australia — Australian school culture, classroom English for teachers, Australian slang, placement confidence and workshops.",
    keywords: "international pre-service teachers Australia, international students teaching degree Australia, teaching in Australia as an international student, Australian school culture, classroom English for teachers, Australian slang for teachers",
    heading: "International teachers",
    summary: [
      "A dedicated program for international pre-service teachers studying in Australia: Australian school culture, staffroom expectations, professional language and parent communication.",
      "Workshops cover classroom English for teachers, Australian slang for teachers, cultural differences in the classroom, the emotional reality of placement, staffroom culture and Australian school sectors.",
      "Bilingual (中文) guides and a full series of editable workshop slide decks are available to download.",
    ],
  },
  "/career-support": {
    title: "Teaching job applications, resumes & interview preparation | Teaching Pathways",
    description:
      "Graduate teacher interview coaching, teaching interview questions used in Australia, key selection criteria (Vic) writing, teaching resume and cover letter reviews, and mock teaching interviews.",
    keywords: "teaching interview questions Australia, graduate teacher interview, key selection criteria teaching, teaching job applications, teaching resume, teaching cover letter, mock teaching interview, first teaching job",
    heading: "Career and employability support",
    summary: [
      "Employability support for graduate teachers chasing their first teaching job: application strategy, teaching resumes and cover letters, key selection criteria responses and mock teaching interviews.",
      "Includes the Interview Mastery Pack and one-to-one coaching with a practising teacher mentor.",
    ],
  },
  "/mentors-schools": {
    title: "Mentor teacher and school partnerships | Teaching Pathways",
    description:
      "Professional learning for mentor teachers and schools supervising pre-service teachers — mentoring frameworks, feedback practice, placement structures and editable workshop slide decks.",
    keywords: "mentor teacher, mentoring pre-service teachers, school partnerships, teacher professional learning, placement supervision",
    heading: "Mentors and schools",
    summary: [
      "Professional learning and ready-made materials for mentor teachers, learning specialists and schools hosting pre-service teachers.",
      "Includes mentoring frameworks, feedback and assessment practice, academic integrity guidance and fully editable workshop slide decks schools can adapt.",
    ],
  },
  "/universities": {
    title: "University partnerships | Teaching Pathways",
    description:
      "Teaching Pathways partners with Australian initial teacher education providers to deliver placement readiness, employability and international student support programs.",
    keywords: "initial teacher education, university partnerships, placement readiness, employability program, pre-service teacher support",
    heading: "Universities",
    summary: [
      "Partnership programs for initial teacher education providers: placement readiness workshops, employability series and dedicated support for international students in teaching degrees.",
    ],
  },
  "/tutoring": {
    title: "Tutoring marketplace | Teaching Pathways",
    description:
      "Find qualified teachers and pre-service teachers for tutoring across primary and secondary subjects, with Working with Children Check details listed on every profile.",
    keywords: "teacher tutoring Australia, find a tutor, pre-service teacher tutor, Working with Children Check teachers, WWCC, Blue Card Queensland",
    heading: "Tutoring marketplace",
    summary: [
      "A marketplace connecting families with qualified teachers and pre-service teachers for tutoring across primary and secondary subjects.",
      "Every profile lists subjects, year levels, mode of delivery and Working with Children Check status (WWCC, NSW Working with Children Check, Queensland Blue Card).",
    ],
  },
  "/resources": {
    title: "Teacher resources & downloads | Teaching Pathways",
    description:
      "Free and premium resources for pre-service and graduate teachers: placement ready toolkit, reflective journal, classroom observation template, interview mastery pack and bilingual guides.",
    keywords: "placement ready toolkit, reflective journal for pre-service teachers, classroom observation template, interview mastery pack for teachers, placement checklist, teacher resources Australia",
    heading: "Free and premium resources",
    summary: [
      "Downloadable resources for pre-service and graduate teachers, with a sample page you can preview before you buy.",
      "Includes the Placement Ready Toolkit, a reflective journal for pre-service teachers, classroom observation templates, placement checklists, the Interview Mastery Pack and bilingual (中文) language and culture guides.",
    ],
  },
  "/workshop-series": {
    title: "Workshop slide deck series for teachers | Teaching Pathways",
    description:
      "Ten editable PowerPoint workshop decks for pre-service teachers, mentors and schools — behaviour management, staffroom culture, teacher anxiety, assessment and academic integrity, and job hunting.",
    keywords: "workshop slide decks teachers, staffroom culture workshop, teacher anxiety workshop, academic integrity workshop, behaviour management workshop, editable PowerPoint teacher professional learning",
    heading: "Ten workshops. One complete library.",
    summary: [
      "Every Teaching Pathways workshop as a fully editable PowerPoint deck: behaviour management, placement and mentor relationships, English and communication anxiety, job hunting and career pathways, staffroom culture, the emotional reality of placement, cultural differences in the classroom, Australian school sectors, practical survival tips, and assessment, feedback and academic integrity.",
      "Single decks are $19, any three are $49, and the complete ten-deck library is $149. Mentors and schools can adapt every slide for their own sessions.",
    ],
  },
  "/ai-workshops": {
    title: "AI workshops for teachers — three professional levels | Teaching Pathways",
    description:
      "A three-level AI professional learning series for educators: Foundation (Thinking Professionally About AI), Intermediate (Working Professionally With AI) and Advanced (Designing Professional AI Systems). Five workshop slide decks per level for $49.",
    keywords: "AI professional learning for teachers, AI workshops for educators, AI in the classroom professional development, teacher AI judgement, AI policy for schools, editable AI workshop slides",
    heading: "Use AI where it helps. Be human where it matters.",
    summary: [
      "A three-level AI professional learning series for any educator — pre-service, graduate, classroom teacher or school leader. Each level is $49 and includes all five workshop slide decks.",
      "Foundation — Thinking Professionally About AI is available now: where AI belongs in professional practice, deconstructing work before delegating it, what an AI actually knows, evidence versus inference, and building a human–AI workflow with judgement checkpoints. Intermediate and Advanced are coming soon.",
    ],
  },
  "/blog": {
    title: "Blog | Teaching Pathways",
    description:
      "Articles on teaching placement, graduate teacher interviews, classroom practice and teaching in Australia as an international student.",
    keywords: "teaching placement blog, graduate teacher advice, teaching interview tips, international teachers Australia",
    heading: "Blog",
    summary: ["Articles and practical guides on placement, employability, classroom practice and teaching in Australia."],
  },
  "/contact": {
    title: "Contact Teaching Pathways",
    description:
      "Get in touch with Teaching Pathways about workshops, coaching, mentoring, tutoring, school and university partnerships, or resource enquiries.",
    keywords: "contact Teaching Pathways, teacher mentoring enquiry, workshop booking Australia",
    heading: "Contact us",
    summary: ["Enquire about workshops, one-to-one coaching, mentoring, tutoring, resources, or school and university partnerships."],
  },
};

export const PRERENDER_ROUTES = Object.keys(PAGES);

export function pageMeta(pathname) {
  const clean = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const page = PAGES[clean] || {};
  return {
    path: clean,
    title: page.title || DEFAULT_TITLE,
    description: page.description || DEFAULT_DESCRIPTION,
    keywords: page.keywords || "",
    canonical: `${SITE_URL}${clean === "/" ? "/" : clean}`,
    heading: page.heading || SITE_NAME,
    summary: page.summary || [],
  };
}

export function organisationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: SITE_NAME,
    alternateName: "Teaching Pathways Australia",
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description: DEFAULT_DESCRIPTION,
    slogan: "Learn · Mentor · Grow",
    areaServed: [{ "@type": "Country", name: "Australia" }, { "@type": "Country", name: "New Zealand" }],
    knowsAbout: [
      "pre-service teacher mentoring", "teaching placement", "graduate teacher employability",
      "teaching interview preparation", "key selection criteria", "international pre-service teachers in Australia",
      "Australian school culture", "behaviour management for student teachers", "mentor teacher professional learning",
    ],
    sameAs: [SITE_URL],
  };
}

export function productJsonLd({ id, title, description, price }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description,
    sku: id,
    brand: { "@type": "Brand", name: SITE_NAME },
    url: `${SITE_URL}/resources`,
    offers: {
      "@type": "Offer",
      price: Number(price).toFixed(2),
      priceCurrency: "AUD",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/resources`,
    },
  };
}
